import React, { useMemo, useState, useRef } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Modal,
  Typography,
  Divider,
  Alert,
} from "@mui/material";
import { toast } from "material-react-toastify";
import { handleSpringDataMutationRequest } from "../../api";
import { razorPayKey } from "../CalendarPage/constants/AmplitudeConstants";
import { channelLogoMap } from "../CalendarPage/constants/Calendar-constants";

/**
 * Account-level Pay Now for total outstanding (net after TDS).
 * One credit clears all dues via bulk capture.
 *
 * Flow (order-driven):
 *   1. POST create-account-dues-order → get order_id + amounts
 *   2. Open Razorpay checkout with order_id (server-created order)
 *   3. On payment success, POST capture-account-dues-payment with payment_id
 *
 * Backend PR: https://github.com/basarooms/backend-core/pull/10139
 */
const AccountPayNowModal = ({
  open,
  onClose,
  groupId,
  hotelId,
  accountInfo,
  currSymbol,
  grossOutstanding,
  onPaymentSuccess,
}) => {
  const [loading, setLoading] = useState(false);
  const [errorShow, setErrorShow] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [razorpayFinalKey] = useState(razorPayKey);
  const [imageUrl] = useState(channelLogoMap.STAYFLEXI_OM);

  const orderDataRef = useRef(null);

  const grossAmount = useMemo(() => {
    const raw = Number(grossOutstanding);
    if (!Number.isFinite(raw) || raw <= 0) return 0;
    return Math.abs(raw);
  }, [grossOutstanding]);

  // TDS % from existing account get-single payload (ACCOUNTS.tds_percentage).
  // Do NOT call nonexistent get-tds-config.
  const tdsPercentage = useMemo(() => {
    const fromDetails = accountInfo?.account_details?.tds_percentage;
    if (fromDetails !== undefined && fromDetails !== null && fromDetails !== "") {
      const n = Number(fromDetails);
      return Number.isFinite(n) ? n : 0;
    }
    const fromRoot = accountInfo?.tds_percentage;
    if (fromRoot !== undefined && fromRoot !== null && fromRoot !== "") {
      const n = Number(fromRoot);
      return Number.isFinite(n) ? n : 0;
    }
    return 0;
  }, [accountInfo]);

  // TDS calculated on GST-exclusive base (gross / 1.18) for India accounts.
  const taxableAmount = grossAmount / 1.18;
  const tdsAmount = (taxableAmount * tdsPercentage) / 100;
  const netPayable = Math.max(0, grossAmount - tdsAmount);

  const buildCapturePayload = (razorpayPaymentId, razorpayOrderId) => {
    const order = orderDataRef.current;
    const amountFromOrder = order?.amount_paise != null ? order.amount_paise / 100 : netPayable;
    const grossFromOrder = order?.gross_amount_paise != null ? order.gross_amount_paise / 100 : grossAmount;
    const tdsPercentFromOrder = order?.tds_percentage ?? tdsPercentage;
    const tdsAmtFromOrder = order?.tds_amount_paise != null ? order.tds_amount_paise / 100 : tdsAmount;

    return {
      group_id: groupId,
      hotel_id: hotelId,
      "razorpay-pg-id": razorpayPaymentId,
      razorpay_payment_id: razorpayPaymentId,
      razorpay_order_id: razorpayOrderId || order?.order_id,
      amount: amountFromOrder,
      currency: order?.currency || "INR",
      gross_amount: grossFromOrder,
      tds_percentage: tdsPercentFromOrder,
      tds_amount: tdsAmtFromOrder,
      net_payable: amountFromOrder,
      net_amount: amountFromOrder,
      // No ledger_id — account-level bulk clear of all dues.
    };
  };

  const buildRazorpayOptions = (orderId, orderKeyId) => ({
    // Platform key id the order was created with, when the backend returns it.
    key: orderKeyId || razorpayFinalKey,
    order_id: orderId,
    currency: orderDataRef.current?.currency || "INR",
    name: "Stayflexi",
    description: "Pay outstanding account dues",
    image: imageUrl,
    handler: function (response) {
      setLoading(true);
      const payload = buildCapturePayload(
        response.razorpay_payment_id,
        response.razorpay_order_id
      );

      handleSpringDataMutationRequest(
        "POST",
        `core/api/v1/payments/razorpay/capture-account-dues-payment/?groupId=${groupId}&hotelId=${hotelId}`,
        payload
      )
        .then((resp) => {
          toast.success(
            resp?.message || resp || "Payment successful! Account dues cleared."
          );
          setLoading(false);
          onPaymentSuccess && onPaymentSuccess();
          onClose();
        })
        .catch((err) => {
          console.error("Account dues payment capture error:", err);
          toast.error(
            err?.response?.data?.message ||
              err?.message ||
              "Payment capture failed. Please contact support."
          );
          setLoading(false);
        });
    },
    prefill: {
      name: accountInfo?.account_details?.account_name || "",
      email: accountInfo?.account_details?.account_billing_email || "",
      contact: accountInfo?.account_details?.account_billing_phone || "",
    },
    notes: {
      account_id: groupId,
      module_source: "ACCOUNT_DUES_PAYMENT",
      gross_amount: String(grossAmount),
      tds_percentage: String(tdsPercentage),
      net_payable: String(netPayable),
    },
    theme: {
      color: "#1ab394",
      hide_topbar: false,
    },
  });

  const createOrderPayload = () => ({
    group_id: groupId,
    hotel_id: hotelId,
    gross_amount: grossAmount,
    tds_percentage: tdsPercentage,
    currency: "INR",
  });

  const handlePayNow = async () => {
    if (netPayable <= 0) {
      toast.error("Invalid payment amount");
      return;
    }

    setLoading(true);
    try {
      const orderResp = await handleSpringDataMutationRequest(
        "POST",
        `core/api/v1/payments/razorpay/create-account-dues-order?groupId=${groupId}`,
        createOrderPayload()
      );

      if (!orderResp?.order_id) {
        throw new Error("No order_id in response");
      }

      orderDataRef.current = orderResp;

      const rzp = new window.Razorpay(
        buildRazorpayOptions(orderResp.order_id, orderResp.key_id)
      );
      rzp.open();
      rzp.on("payment.failed", function (response) {
        setErrorShow(true);
        setErrorMessage(response?.error?.description || "Payment failed");
        setTimeout(() => {
          setErrorShow(false);
        }, 5000);
      });
      setLoading(false);
    } catch (err) {
      console.error("Create order error:", err);
      toast.error(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to create payment order. Please try again."
      );
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="account-pay-now-modal"
      aria-describedby="account-pay-now-description"
    >
      <Box className="settleDuesModal">
        <div className="settleDuesWrapper">
          <div className="settleDuesHeader">
            <div className="settleDuesHeading">Pay outstanding dues</div>
            <div className="close" onClick={onClose}>
              X
            </div>
          </div>

          <div className="amountWrapper">
            {errorShow && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Payment bifurcation
            </Typography>

            <Box
              sx={{
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography>Gross outstanding:</Typography>
                <Typography fontWeight={500}>
                  {currSymbol} {parseFloat(grossAmount).toFixed(2)}
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1.5,
                }}
              >
                <Typography color="text.secondary">
                  Account TDS ({tdsPercentage}%):
                </Typography>
                <Typography
                  color={tdsPercentage > 0 ? "error.main" : "text.secondary"}
                  fontWeight={500}
                >
                  {tdsPercentage > 0 ? "-" : ""} {currSymbol}{" "}
                  {parseFloat(tdsAmount).toFixed(2)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6" fontWeight={600}>
                  Net payable:
                </Typography>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                  {currSymbol} {parseFloat(netPayable).toFixed(2)}
                </Typography>
              </Box>
            </Box>

            {tdsPercentage > 0 ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                TDS of {tdsPercentage}% is applied at account level. One payment
                clears all outstanding dues. You will be charged the net payable
                of {currSymbol} {parseFloat(netPayable).toFixed(2)}.
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                One payment clears all outstanding dues for this account.
              </Alert>
            )}

            <div className="settleDuesSubmitBtns">
              <Button className="cancelBtn" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              <Button
                className="submit"
                variant="custom-button"
                onClick={handlePayNow}
                disabled={loading || netPayable <= 0}
              >
                {loading ? (
                  <CircularProgress size={15} color="inherit" sx={{ ml: 1.5 }} />
                ) : (
                  `Pay ${currSymbol} ${parseFloat(netPayable).toFixed(2)}`
                )}
              </Button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default AccountPayNowModal;
